import { useEffect, useRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DemoEntity {
  type: string;
  dotClass: string;
  shadowClass: string;
  name: string;
  badge: string;
  badgeBgClass: string;
  badgeTextClass: string;
}

interface CanvasNode {
  id: string;
  borderClass: string;
  glowClass?: string;
  type: string;
  dotClass: string;
  title: string;
  badge: string;
  badgeBgClass: string;
  badgeTextClass: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

interface CanvasEdge {
  from: string;
  to: string;
  stroke: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DEMO_TRANSCRIPT =
  "Je dois organiser la renovation de la maison. Il faut trouver un electricien, c'est prioritaire. Refaire la plomberie, ca coutera environ 3 500 euros. Peindre le salon aussi. Budget total 15 000 euros. Rendez-vous avec l'architecte le 15 avril. Marc Durand, c'est l'electricien recommande.";

const DEMO_ENTITIES: DemoEntity[] = [
  {
    type: 'objective',
    dotClass: 'bg-violet-500',
    shadowClass: 'shadow-[0_0_6px_rgba(139,92,246,0.4)]',
    name: 'Renovation maison',
    badge: '15 000 \u20ac',
    badgeBgClass: 'bg-violet-500/15',
    badgeTextClass: 'text-violet-300',
  },
  {
    type: 'task',
    dotClass: 'bg-blue-400',
    shadowClass: 'shadow-[0_0_6px_rgba(96,165,250,0.4)]',
    name: 'Trouver electricien',
    badge: 'Haute',
    badgeBgClass: 'bg-blue-400/15',
    badgeTextClass: 'text-blue-300',
  },
  {
    type: 'task',
    dotClass: 'bg-blue-400',
    shadowClass: 'shadow-[0_0_6px_rgba(96,165,250,0.4)]',
    name: 'Refaire plomberie',
    badge: '3 500 \u20ac',
    badgeBgClass: 'bg-blue-400/15',
    badgeTextClass: 'text-blue-300',
  },
  {
    type: 'task',
    dotClass: 'bg-blue-400',
    shadowClass: 'shadow-[0_0_6px_rgba(96,165,250,0.4)]',
    name: 'Peindre le salon',
    badge: 'Moyenne',
    badgeBgClass: 'bg-blue-400/15',
    badgeTextClass: 'text-blue-300',
  },
  {
    type: 'event',
    dotClass: 'bg-amber-400',
    shadowClass: 'shadow-[0_0_6px_rgba(245,158,11,0.4)]',
    name: 'RDV architecte',
    badge: '15 avril',
    badgeBgClass: 'bg-amber-400/15',
    badgeTextClass: 'text-amber-300',
  },
  {
    type: 'contact',
    dotClass: 'bg-emerald-400',
    shadowClass: 'shadow-[0_0_6px_rgba(52,211,153,0.4)]',
    name: 'Marc Durand',
    badge: 'Electricien',
    badgeBgClass: 'bg-emerald-400/15',
    badgeTextClass: 'text-emerald-300',
  },
];

// ─── CSS (injected) ───────────────────────────────────────────────────────────

const DEMO_CSS = `
.pipeline-line {
  width: 2px;
  background: rgba(63, 63, 70, 0.6);
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
}
.pipeline-line-fill {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 0%;
  background: linear-gradient(to bottom, rgb(139, 92, 246), rgba(139, 92, 246, 0.15));
  transition: height 1.2s ease-out;
}
.pipeline-stage.stage-active .pipeline-line-fill {
  height: 100%;
}
.stage-num-circle {
  transition: all 0.5s ease;
}
.pipeline-stage.stage-active .stage-num-circle {
  background: rgb(139, 92, 246) !important;
  color: white !important;
  border-color: rgb(139, 92, 246) !important;
  box-shadow: 0 0 16px rgba(139, 92, 246, 0.4);
}
.demo-waveform {
  display: flex;
  align-items: center;
  gap: 2px;
  height: 32px;
}
.demo-wave-bar {
  width: 2.5px;
  background: rgb(139, 92, 246);
  border-radius: 2px;
  height: 6px;
  opacity: 0.5;
}
.demo-wave-bar.animating {
  animation: demoWaveAnim 0.8s ease-in-out infinite alternate;
  opacity: 0.7;
}
@keyframes demoWaveAnim {
  0% { height: 6px; }
  100% { height: var(--h); }
}
.type-cursor {
  display: inline-block;
  width: 2px;
  height: 1em;
  background: rgb(139, 92, 246);
  vertical-align: text-bottom;
  margin-left: 2px;
  animation: demoBlink 0.8s step-end infinite;
}
@keyframes demoBlink { 50% { opacity: 0; } }
.entity-pill {
  opacity: 0;
  transform: translateY(12px) scale(0.92);
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.entity-pill.revealed {
  opacity: 1;
  transform: translateY(0) scale(1);
}
.cv-node {
  position: absolute;
  opacity: 0;
  transform: scale(0);
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.cv-node.revealed {
  opacity: 1;
  transform: scale(1);
}
.cv-edge {
  fill: none;
  stroke-linecap: round;
}
.objective-glow {
  animation: demoObjPulse 2.5s ease-in-out infinite alternate;
}
@keyframes demoObjPulse {
  0% { box-shadow: 0 0 16px rgba(139,92,246,0.15); }
  100% { box-shadow: 0 0 28px rgba(139,92,246,0.3); }
}
`;

// ─── Gradient text style (same as LandingPage) ────────────────────────────────

const gradientTextStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #ffffff 0%, #a3a3a3 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function SynapseDemoSection() {
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const sectionRef = useRef<HTMLElement>(null);
  const waveformRef = useRef<HTMLDivElement>(null);
  const transcriptRef = useRef<HTMLParagraphElement>(null);
  const extractionRef = useRef<HTMLDivElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const canvasSvgRef = useRef<SVGSVGElement>(null);
  const waveformInitRef = useRef(false);
  const [activatedStages, setActivatedStages] = useState<Set<string>>(new Set());

  // Build waveform bars on mount
  useEffect(() => {
    if (waveformInitRef.current || !waveformRef.current) return;
    waveformInitRef.current = true;
    const wf = waveformRef.current;
    for (let i = 0; i < 55; i++) {
      const bar = document.createElement('div');
      bar.className = 'demo-wave-bar';
      bar.style.setProperty('--h', Math.random() * 22 + 6 + 'px');
      bar.style.animationDelay = Math.random() * 0.8 + 's';
      wf.appendChild(bar);
    }
  }, []);

  // Stage triggers
  const triggerStage1 = useCallback(() => {
    waveformRef.current
      ?.querySelectorAll('.demo-wave-bar')
      .forEach((b) => b.classList.add('animating'));
  }, []);

  const triggerStage2 = useCallback(() => {
    const el = transcriptRef.current;
    if (!el) return;
    el.innerHTML = '';
    let i = 0;
    function type() {
      if (!el) return;
      if (i < DEMO_TRANSCRIPT.length) {
        el.innerHTML = DEMO_TRANSCRIPT.substring(0, i + 1) + '<span class="type-cursor"></span>';
        i++;
        setTimeout(type, 18);
      } else {
        el.innerHTML =
          DEMO_TRANSCRIPT +
          ' <span class="inline-flex items-center gap-1.5 ml-2 text-emerald-400 text-xs font-medium"><span class="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>' +
          tRef.current('landing.demo_transcribed') +
          '</span>';
      }
    }
    type();
  }, []);

  const triggerStage3 = useCallback(() => {
    const grid = extractionRef.current;
    if (!grid) return;
    grid.innerHTML = '';
    DEMO_ENTITIES.forEach((ent, i) => {
      const pill = document.createElement('div');
      pill.className =
        'entity-pill bg-zinc-900/50 border border-zinc-800 rounded-lg px-3.5 py-2.5 flex items-center gap-2.5 text-sm';
      pill.innerHTML = `
        <span class="w-2 h-2 rounded-full ${ent.dotClass} ${ent.shadowClass} flex-shrink-0"></span>
        <span class="text-zinc-200 font-medium">${ent.name}</span>
        <span class="text-[10px] px-1.5 py-0.5 rounded ${ent.badgeBgClass} ${ent.badgeTextClass} font-medium ml-auto">${ent.badge}</span>
      `;
      grid.appendChild(pill);
      setTimeout(() => pill.classList.add('revealed'), 250 + i * 280);
    });
  }, []);

  const triggerStage4 = useCallback(() => {
    const container = canvasContainerRef.current;
    const svg = canvasSvgRef.current;
    if (!container || !svg) return;

    // Clear previous
    container.querySelectorAll('.cv-node').forEach((n) => n.remove());
    svg.innerHTML = '';

    const cW = container.clientWidth;
    const cH = container.clientHeight;
    svg.setAttribute('viewBox', `0 0 ${cW} ${cH}`);

    const NW = 130,
      NH = 56;
    const midX = cW / 2;

    const nodes: CanvasNode[] = [
      {
        id: 'obj',
        borderClass: 'border-violet-500/50',
        glowClass: 'objective-glow',
        type: 'Objectif',
        dotClass: 'bg-violet-500',
        title: 'Renovation maison',
        badge: '15 000 \u20ac',
        badgeBgClass: 'bg-violet-500/15',
        badgeTextClass: 'text-violet-300',
        x: midX - (NW + 10) / 2,
        y: cH - NH - 20,
        w: NW + 10,
        h: NH,
      },
      {
        id: 't1',
        borderClass: 'border-blue-400/30',
        type: 'Tache',
        dotClass: 'bg-blue-400',
        title: 'Trouver electricien',
        badge: 'Haute',
        badgeBgClass: 'bg-blue-400/15',
        badgeTextClass: 'text-blue-300',
        x: cW * 0.06,
        y: cH * 0.42,
        w: NW,
        h: NH,
      },
      {
        id: 't2',
        borderClass: 'border-blue-400/30',
        type: 'Tache',
        dotClass: 'bg-blue-400',
        title: 'Refaire plomberie',
        badge: '3 500 \u20ac',
        badgeBgClass: 'bg-blue-400/15',
        badgeTextClass: 'text-blue-300',
        x: cW * 0.28,
        y: cH * 0.42,
        w: NW,
        h: NH,
      },
      {
        id: 't3',
        borderClass: 'border-blue-400/30',
        type: 'Tache',
        dotClass: 'bg-blue-400',
        title: 'Peindre le salon',
        badge: 'Moyenne',
        badgeBgClass: 'bg-blue-400/15',
        badgeTextClass: 'text-blue-300',
        x: cW * 0.52,
        y: cH * 0.42,
        w: NW,
        h: NH,
      },
      {
        id: 't4',
        borderClass: 'border-blue-400/30',
        type: 'Tache',
        dotClass: 'bg-blue-400',
        title: 'Commander materiaux',
        badge: '2 200 \u20ac',
        badgeBgClass: 'bg-blue-400/15',
        badgeTextClass: 'text-blue-300',
        x: cW * 0.76,
        y: cH * 0.42,
        w: NW,
        h: NH,
      },
      {
        id: 'ev',
        borderClass: 'border-amber-400/30',
        type: 'Evenement',
        dotClass: 'bg-amber-400',
        title: 'RDV architecte',
        badge: '15 avril',
        badgeBgClass: 'bg-amber-400/15',
        badgeTextClass: 'text-amber-300',
        x: cW * 0.4,
        y: cH * 0.06,
        w: NW,
        h: NH,
      },
      {
        id: 'co',
        borderClass: 'border-emerald-400/30',
        type: 'Contact',
        dotClass: 'bg-emerald-400',
        title: 'Marc Durand',
        badge: 'Electricien',
        badgeBgClass: 'bg-emerald-400/15',
        badgeTextClass: 'text-emerald-300',
        x: cW * 0.06,
        y: cH * 0.08,
        w: NW,
        h: NH,
      },
    ];

    const edges: CanvasEdge[] = [
      { from: 't1', to: 'obj', stroke: 'rgba(139,92,246,0.4)' },
      { from: 't2', to: 'obj', stroke: 'rgba(139,92,246,0.4)' },
      { from: 't3', to: 'obj', stroke: 'rgba(139,92,246,0.4)' },
      { from: 't4', to: 'obj', stroke: 'rgba(139,92,246,0.4)' },
      { from: 'ev', to: 'obj', stroke: 'rgba(245,158,11,0.35)' },
      { from: 'co', to: 't1', stroke: 'rgba(52,211,153,0.35)' },
    ];

    const nodeMap: Record<string, CanvasNode> = {};
    nodes.forEach((n, i) => {
      nodeMap[n.id] = n;
      const el = document.createElement('div');
      el.className = `cv-node bg-[#0a0a10] border rounded-lg px-3 py-2 ${n.borderClass} ${n.glowClass || ''}`;
      el.style.cssText = `left:${n.x}px; top:${n.y}px; width:${n.w}px; z-index:2;`;
      el.innerHTML = `
        <div class="flex items-center gap-1.5 mb-1">
          <span class="w-[5px] h-[5px] rounded-full ${n.dotClass}"></span>
          <span class="text-[9px] uppercase tracking-[0.12em] text-zinc-500">${n.type}</span>
        </div>
        <p class="text-[11px] font-medium text-zinc-200 mb-1">${n.title}</p>
        <span class="text-[9px] px-1.5 py-0.5 rounded ${n.badgeBgClass} ${n.badgeTextClass}">${n.badge}</span>
      `;
      container.appendChild(el);

      let delay: number;
      if (n.id === 'obj') delay = 200;
      else if (n.id.startsWith('t')) delay = 500 + i * 200;
      else delay = 1500 + (i - 5) * 250;
      setTimeout(() => el.classList.add('revealed'), delay);
    });

    edges.forEach((edge, i) => {
      const from = nodeMap[edge.from];
      const to = nodeMap[edge.to];
      if (!from || !to) return;

      const x1 = from.x + from.w / 2;
      const y1 = from.y + from.h;
      const x2 = to.x + to.w / 2;
      const y2 = to.y;
      const dy = Math.abs(y2 - y1);
      const t = Math.min(Math.max(dy * 0.55, 35), 140);
      const d = `M ${x1} ${y1} C ${x1} ${y1 + t}, ${x2} ${y2 - t}, ${x2} ${y2}`;

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', d);
      path.classList.add('cv-edge');
      path.setAttribute('stroke', edge.stroke);
      path.setAttribute('stroke-width', '1.5');
      path.setAttribute('pathLength', '100');
      path.style.strokeDasharray = '100';
      path.style.strokeDashoffset = '100';
      path.style.transition = 'stroke-dashoffset 0.7s ease-out';
      svg.appendChild(path);

      setTimeout(
        () => {
          path.style.strokeDashoffset = '0';
        },
        2000 + i * 180
      );
    });
  }, []);

  // Stage observer
  useEffect(() => {
    const triggers: Record<string, () => void> = {
      '1': triggerStage1,
      '2': triggerStage2,
      '3': triggerStage3,
      '4': triggerStage4,
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const stage = (entry.target as HTMLElement).dataset.stage;
          if (entry.isIntersecting && stage && !activatedStages.has(stage)) {
            setActivatedStages((prev) => new Set(prev).add(stage));
            entry.target.classList.add('stage-active');
            triggers[stage]?.();
          }
        });
      },
      { threshold: 0.35 }
    );

    const stages = sectionRef.current?.querySelectorAll('.pipeline-stage');
    stages?.forEach((s) => observer.observe(s));

    return () => observer.disconnect();
  }, [activatedStages, triggerStage1, triggerStage2, triggerStage3, triggerStage4]);

  return (
    <section ref={sectionRef} id="demo-synapse" className="py-20 md:py-32 px-6">
      <style>{DEMO_CSS}</style>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16 reveal">
          <span className="inline-flex items-center gap-2 mb-6 px-3 py-1 text-xs font-medium tracking-wide text-purple-400 border border-purple-500/30 rounded-full">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor" stroke="none">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
            Demo en direct
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold mb-3" style={gradientTextStyle}>
            {t('landing.demo_heading')}
          </h2>
          <p className="text-zinc-400 text-base sm:text-lg max-w-2xl mx-auto">
            {t('landing.demo_description')}
          </p>
        </div>

        {/* Pipeline timeline */}
        <div className="flex flex-col gap-0 max-w-3xl mx-auto">
          {/* Stage 1: Capture */}
          <div className="pipeline-stage flex gap-0 reveal" data-stage="1">
            <div className="flex flex-col items-center w-14 flex-shrink-0">
              <div className="stage-num-circle w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold bg-zinc-900/80 border border-zinc-700 text-zinc-500">
                1
              </div>
              <div className="pipeline-line flex-1">
                <div className="pipeline-line-fill" />
              </div>
            </div>
            <div className="flex-1 pb-10 pl-5">
              <p className="text-[11px] uppercase tracking-[0.2em] text-purple-400 font-semibold mb-1.5">
                {t('landing.demo_capture_title')}
              </p>
              <p className="text-base font-medium text-white mb-4">
                {t('landing.demo_capture_desc')}
              </p>
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 relative overflow-hidden">
                <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-violet-500 rounded-l-xl" />
                <div className="flex items-center gap-4 pl-2">
                  <svg className="w-8 h-8 flex-shrink-0" viewBox="0 0 40 40" fill="none">
                    <circle
                      cx="20"
                      cy="20"
                      r="16"
                      fill="rgba(139,92,246,0.1)"
                      stroke="rgba(139,92,246,0.3)"
                      strokeWidth="1"
                    />
                    <rect x="16" y="10" width="8" height="14" rx="4" fill="rgb(139,92,246)" />
                    <path
                      d="M12 22c0 6 4 8 8 8s8-2 8-8"
                      stroke="rgb(139,92,246)"
                      strokeWidth="1.5"
                      fill="none"
                      strokeLinecap="round"
                    />
                    <line
                      x1="20"
                      y1="30"
                      x2="20"
                      y2="34"
                      stroke="rgb(139,92,246)"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div ref={waveformRef} className="demo-waveform flex-1" />
                </div>
              </div>
              <p className="mt-3 text-sm text-zinc-500 italic pl-4 border-l-2 border-violet-500/25 leading-relaxed">
                {t('landing.demo_capture_quote')}
              </p>
            </div>
          </div>

          {/* Stage 2: Transcription */}
          <div className="pipeline-stage flex gap-0 reveal" data-stage="2">
            <div className="flex flex-col items-center w-14 flex-shrink-0">
              <div className="stage-num-circle w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold bg-zinc-900/80 border border-zinc-700 text-zinc-500">
                2
              </div>
              <div className="pipeline-line flex-1">
                <div className="pipeline-line-fill" />
              </div>
            </div>
            <div className="flex-1 pb-10 pl-5">
              <p className="text-[11px] uppercase tracking-[0.2em] text-purple-400 font-semibold mb-1.5">
                {t('landing.demo_transcription_title')}
              </p>
              <p className="text-base font-medium text-white mb-4">
                {t('landing.demo_transcription_desc')}
              </p>
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 relative overflow-hidden">
                <div
                  className="absolute top-0 left-0 right-0 h-[2px]"
                  style={{
                    background: 'linear-gradient(90deg, transparent, rgb(139,92,246), transparent)',
                  }}
                />
                <p
                  ref={transcriptRef}
                  className="text-sm text-zinc-300 leading-relaxed font-light"
                />
              </div>
            </div>
          </div>

          {/* Stage 3: Extraction */}
          <div className="pipeline-stage flex gap-0 reveal" data-stage="3">
            <div className="flex flex-col items-center w-14 flex-shrink-0">
              <div className="stage-num-circle w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold bg-zinc-900/80 border border-zinc-700 text-zinc-500">
                3
              </div>
              <div className="pipeline-line flex-1">
                <div className="pipeline-line-fill" />
              </div>
            </div>
            <div className="flex-1 pb-10 pl-5">
              <p className="text-[11px] uppercase tracking-[0.2em] text-purple-400 font-semibold mb-1.5">
                {t('landing.demo_structure_title')}
              </p>
              <p className="text-base font-medium text-white mb-4">
                {t('landing.demo_structure_desc')}
              </p>
              <div ref={extractionRef} className="flex flex-wrap gap-2.5" />
            </div>
          </div>

          {/* Stage 4: Canvas projection */}
          <div className="pipeline-stage flex gap-0 reveal" data-stage="4">
            <div className="flex flex-col items-center w-14 flex-shrink-0">
              <div className="stage-num-circle w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold bg-zinc-900/80 border border-zinc-700 text-zinc-500">
                4
              </div>
            </div>
            <div className="flex-1 pl-5">
              <p className="text-[11px] uppercase tracking-[0.2em] text-purple-400 font-semibold mb-1.5">
                {t('landing.demo_projection_title')}
              </p>
              <p className="text-base font-medium text-white mb-4">
                {t('landing.demo_projection_desc')}
              </p>
              <div
                ref={canvasContainerRef}
                className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden relative"
                style={{ height: 320 }}
              >
                <svg
                  ref={canvasSvgRef}
                  className="absolute inset-0 w-full h-full"
                  style={{ zIndex: 1 }}
                  xmlns="http://www.w3.org/2000/svg"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
